import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaWater, FaSearch, FaHome,FaTimes, FaPlus, FaPen, FaArrowLeft, FaDollarSign } from 'react-icons/fa';
import { StandardURL } from "../App";

// Set app element for the modal for accessibility
Modal.setAppElement('#root'); // Assuming your root element has id="root"

const WaterUnitsPage = () => {
    const [waterUnits, setWaterUnits] = useState({
        units: null,
        amount: null,
        water_unit_id: null,
    }); // State for water units as an object
    const [showAddModal, setShowAddModal] = useState(false); // State for showing add modal
    const [units, setUnits] = useState(""); // State for units in modal (use string for input)
    const [amount, setAmount] = useState(""); // State for amount in modal (use string for input)
    const [propertyId, setPropertyId] = useState(""); // State for propertyId input
    const [isLoading, setIsLoading] = useState(false); // For form submission loading state
    const [isFetching, setIsFetching] = useState(false); // For fetch-specific loading
    const navigate = useNavigate();

    // Debounced fetch on propertyId change
    useEffect(() => {
        const token = localStorage.getItem("token");

        if (propertyId) {
            setIsFetching(true); // Start fetching indicator
            // Clear previous data while fetching new data
            setWaterUnits({ units: null, amount: null, water_unit_id: null });

            axios
                .get(
                    `${StandardURL}/iNyumba/waterunits/fetchonewaterunit/${propertyId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )
                .then((response) => {
                    const waterUnitsData = response.data.response;

                    if (Array.isArray(waterUnitsData) && waterUnitsData.length > 0) {
                        const { units, amount, water_unit_id } = waterUnitsData[0];
                        setWaterUnits({ units, amount, water_unit_id });
                        toast.success("Water unit data fetched successfully!");
                    } else {
                        // Reset waterUnits if no record is found
                        setWaterUnits({
                            units: null,
                            amount: null,
                            water_unit_id: null,
                        });
                        toast.info(`No existing water unit record found for Property ID: ${propertyId}`);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching water units:", error);
                    // Reset waterUnits on error
                    setWaterUnits({ units: null, amount: null, water_unit_id: null });
                    toast.error(error.response?.data?.response || "Failed to fetch water unit data. Check ID.");
                })
                .finally(() => {
                    setIsFetching(false); // Stop fetching indicator
                });
        } else {
            // Clear data if input is empty
            setWaterUnits({ units: null, amount: null, water_unit_id: null });
        }
    }, [propertyId]); // Trigger the effect whenever propertyId changes

    const handleAddWaterUnits = () => {
        if (!propertyId || !units || !amount || isNaN(parseFloat(units)) || isNaN(parseFloat(amount))) {
            toast.error("Please enter a valid Property ID, Units, and Amount.");
            return;
        }

        setIsLoading(true); // Set loading state to true
        
        // Create FormData object
        const formData = new FormData();
        formData.append("units", units);
        formData.append("amount", amount);

        const token = localStorage.getItem("token");

        // Send POST request to add water units
        axios
            .post(
                `${StandardURL}/iNyumba/waterunits/postwaterunits/${propertyId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response || "Water units added successfully!");
                setIsLoading(false); // Set loading state to false
                setShowAddModal(false); // Close the modal after adding water units
                setUnits(""); // Clear modal input
                setAmount(""); // Clear modal input
                // Manually trigger a fetch to update the display
                // Note: The useEffect already handles fetching when propertyId changes, but we force it here for immediate update
                setPropertyId(propertyId + " "); // Trick to trigger useEffect without changing the actual ID
                setPropertyId(propertyId); // Resetting ID back
            })
            .catch((error) => {
                toast.error(error.response?.data?.response || "Failed to add water units.");
                console.error("Error adding water units:", error);
            })
            .finally(() => {
                setIsLoading(false); // Reset loading state in case of error
            });
    };

    const handleUpdateWaterUnits = () => {
        // Only navigate if a record was successfully fetched
        if (waterUnits.water_unit_id) {
            localStorage.setItem("waterUnits", JSON.stringify(waterUnits));
            navigate("/admin/waterunitsupdate");
        } else {
            toast.warn("No existing water unit record to update.");
        }
    };

    const handleBack = () => {
        navigate(-1); // Go back to the previous page
    };

    // Helper to format currency
    const formatCurrency = (amount) => {
        const value = parseFloat(amount);
        return isNaN(value) ? 'N/A' : `Ksh ${value.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                   
                    <div className="max-w-3xl mx-auto">
                        {/* Header and Back Button */}
                        <div className="flex items-center justify-between mb-8">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaWater className="mr-3 text-blue-600" /> Manage Water Units
                            </h1>
                            <button
                                onClick={handleBack}
                                className="inline-flex items-center bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md"
                            >
                                <FaArrowLeft className="mr-2" /> Back
                            </button>
                        </div>

                        {/* Search Card */}
                        <div className="bg-white shadow-xl rounded-xl p-6 mb-8 border-t-4 border-blue-500">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <FaSearch className="mr-2 text-blue-500" /> Lookup Property
                            </h2>
                            <label htmlFor="propertyId" className="block text-sm font-medium text-gray-700 mb-2">
                                Enter Property ID:
                            </label>
                            <input
                                type="text"
                                id="propertyId"
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-blue-500 focus:border-blue-500 transition"
                                value={propertyId}
                                onChange={(e) => setPropertyId(e.target.value)}
                                placeholder="Enter property ID to search..."
                            />
                            {isFetching && (
                                <div className="mt-3 flex items-center text-sm text-blue-600">
                                    <Oval height={20} width={20} color="#3b82f6" visible={true} ariaLabel="loading-indicator" secondaryColor="#60a5fa" />
                                    <span className="ml-2">Fetching water unit details...</span>
                                </div>
                            )}
                        </div>

                        {/* Water Unit Details Card (Conditional Display) */}
                        <div className="bg-white shadow-xl rounded-xl p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center border-b pb-2">
                                <FaHome className="mr-2 text-indigo-600" /> Details for Property ID: {propertyId || '...'}
                            </h2>

                            {propertyId && !isFetching && (
                                <div className="space-y-4">
                                    {/* Units Display */}
                                    <div className="bg-blue-50 p-4 rounded-lg flex items-center justify-between border-l-4 border-blue-600">
                                        <div className="flex items-center">
                                            <FaWater className="text-3xl text-blue-600 mr-4" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Current Water Units:</p>
                                                <p className="text-2xl font-extrabold text-blue-600">
                                                    {waterUnits.units !== null ? waterUnits.units : 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Amount Display */}
                                    <div className="bg-green-50 p-4 rounded-lg flex items-center justify-between border-l-4 border-green-600">
                                        <div className="flex items-center">
                                            <FaDollarSign className="text-3xl text-green-600 mr-4" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Price Per Unit (Amount):</p>
                                                <p className="text-2xl font-extrabold text-green-600">
                                                    {waterUnits.amount !== null ? formatCurrency(waterUnits.amount) : 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-col sm:flex-row gap-3 pt-3">
                                        {waterUnits.units !== null ? (
                                            <button
                                                onClick={handleUpdateWaterUnits}
                                                className="w-full sm:w-auto inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                disabled={isLoading}
                                            >
                                                <FaPen className="mr-2" /> Update Units/Amount
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setUnits("");
                                                    setAmount("");
                                                    setShowAddModal(true);
                                                }}
                                                className="w-full sm:w-auto inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                disabled={isLoading}
                                            >
                                                <FaPlus className="mr-2" /> Add Initial Units/Amount
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-sm text-red-500 italic mt-4">
                                        Note: If units/amount already exist, use the **Update** button. Do not add a duplicate entry.
                                    </p>
                                </div>
                            )}

                            {/* Initial/Empty state messages */}
                            {!propertyId && !isFetching && (
                                <p className="text-center text-gray-500 py-8">
                                    Please enter a **Property ID** above to check for an existing water unit record.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal for adding water units */}
                <Modal
                    isOpen={showAddModal}
                    onRequestClose={() => setShowAddModal(false)}
                    contentLabel="Add Water Units Modal"
                    className="modal fixed inset-0 flex items-center justify-center p-4 z-50"
                    overlayClassName="overlay fixed inset-0 bg-gray-900 bg-opacity-75"
                >
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full transform transition-all">
                        <div className="flex justify-between items-center border-b pb-3 mb-4">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                <FaWater className="mr-2 text-blue-600"/> Add Water Units/Amount
                            </h2>
                            <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-red-600 mb-4">
                            **WARNING:** Only use this to set the **initial** water unit record for **Property ID: {propertyId}**. Use the update button after initial setup.
                        </p>

                        <div className="mb-4">
                            <label htmlFor="units" className="block text-sm font-medium text-gray-700 mb-1">
                                Units:
                            </label>
                            <input
                                type="number"
                                id="units"
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-blue-500 focus:border-blue-500 transition"
                                value={units}
                                onChange={(e) => setUnits(e.target.value)}
                                placeholder="Enter units (e.g., 100)"
                                min="0"
                            />
                        </div>
                        <div className="mb-6">
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                                <FaDollarSign className="inline mr-1"/> Amount (Price Per Unit KES):
                            </label>
                            <input
                                type="number"
                                id="amount"
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-blue-500 focus:border-blue-500 transition"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Enter amount (e.g., 50.00)"
                                min="0.01"
                                step="any"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300 transition duration-150"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddWaterUnits}
                                disabled={!units || !amount || isLoading || parseFloat(units) < 0 || parseFloat(amount) <= 0}
                                className={`inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    isLoading
                                        ? "bg-blue-400 text-white cursor-not-allowed"
                                        : "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500"
                                }`}
                            >
                                {isLoading ? (
                                    <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#a5b4fc" />
                                ) : (
                                    "Confirm Add"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            </AuthGuard>
        </EmailGuard>
    );
};

export default WaterUnitsPage;